import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar, NavGroup } from './Sidebar';
import { TopBar, Breadcrumb } from './TopBar';
import GlobalSearch from '../../GlobalSearch';
import { NotificationsDrawer } from '../../NotificationsDrawer';
import ChangelogDrawer from '../../ChangelogDrawer';
import { useUnreadNotifications } from '../../../hooks/useUnreadNotifications';
import { useUnreadChangelog } from '../../../hooks/useUnreadChangelog';
import { QuickCreateMenu, QuickCreateKind } from '../../QuickCreateMenu';

interface AppShellProps {
  nav?: NavGroup[];
  breadcrumbs?: Breadcrumb[];
  topBarActions?: React.ReactNode;
  /** Override the derived unread-notification dot (normally auto-polled). */
  hasNotifications?: boolean;
  /** Remove the main content padding for full-bleed pages (e.g. the inbox). */
  fullBleed?: boolean;
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
  nav,
  breadcrumbs,
  topBarActions,
  hasNotifications,
  fullBleed,
  children,
}) => {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Each choice lands on the screen that already knows how to create that
  // thing, with a query param telling it to open its own form. Re-implementing
  // four creation forms in the header would be four more places to keep in
  // step with the real ones.
  const onQuickCreate = (kind: QuickCreateKind) => {
    switch (kind) {
      case 'quote':    navigate('/quotes?tab=quotes&new=quote'); break;
      case 'project':  navigate('/quotes?tab=deals&new=project'); break;
      // ?create= is the page's existing convention for "open the creation
      // drawer" (used by global search and the telephony unknown-caller flow),
      // and ?tab= picks customers vs leads. Reusing both beats adding a
      // third way to say the same thing.
      case 'customer': navigate('/customers?tab=customers&create='); break;
      case 'lead':     navigate('/customers?tab=leads&create='); break;
    }
  };
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const { count: unreadCount, refresh: refreshUnread } = useUnreadNotifications();
  const { count: unreadChangelogCount, refresh: refreshChangelog } = useUnreadChangelog();

  // Global keyboard shortcut Cmd/Ctrl + K to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <Sidebar
        nav={nav}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="lg:ms-64">
        <TopBar
          breadcrumbs={breadcrumbs}
          actions={topBarActions}
          hasNotifications={hasNotifications ?? unreadCount > 0}
          unreadCount={hasNotifications === undefined ? unreadCount : undefined}
          unreadChangelogCount={unreadChangelogCount}
          onChangelogClick={() => setChangelogOpen(true)}
          onMenuClick={() => setMobileNavOpen(true)}
          onSearchClick={() => setSearchOpen(true)}
          quickCreate={<QuickCreateMenu onPick={onQuickCreate} />}
          onNotificationsClick={() => setNotificationsOpen(true)}
        />
        <main className={fullBleed ? '' : 'p-4 sm:p-6 lg:p-8'}>{children}</main>
      </div>

      {/* Global search palette (cmd/ctrl+K) */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Notifications drawer */}
      <NotificationsDrawer
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        onChanged={refreshUnread}
      />

      {/* What's-new / internal changelog */}
      <ChangelogDrawer
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        onMarkedRead={refreshChangelog}
      />
    </div>
  );
};
